import {
  captureSentryError,
  showApiErrorNotification,
  useApiMutation,
  useInvalidateApiQueries,
} from '@vrplatform/data';
import { showWarnNotification } from '@vrplatform/theme';
import { useCallback } from 'react';
import {
  buildReservationReclassifyMutationBody,
  type ReservationReclassificationArgs,
} from './useReservationReclassificationMutation.utils';

export function useReservationReclassificationMutation(
  reservationId: string | null,
  onSuccess: () => void
) {
  const invalidate = useInvalidateApiQueries();

  const { mutateAsync: updateReservation, isPending: loading } = useApiMutation(
    'put',
    '/reservations/{id}'
  );

  const submit = useCallback(
    async (args: ReservationReclassificationArgs) => {
      if (!reservationId) {
        captureSentryError('Missing reservationId');
        return showWarnNotification({
          title: 'Missing reservation',
          message: 'Please reach out to support if the issue persists.',
        });
      }

      try {
        const body = buildReservationReclassifyMutationBody(args);

        await updateReservation({
          params: {
            path: {
              id: reservationId,
            },
          },
          body,
        });

        invalidate(['reservations']);
        onSuccess();
      } catch (error: any) {
        showApiErrorNotification({
          title: 'Failed to update reservation',
          defaultMessage:
            'We failed to update the reservation. Please try again later and if the problem persists, contact support.',
          error,
        });
      }
    },
    [reservationId, onSuccess, updateReservation, invalidate]
  );

  return {
    loading,
    submit,
  };
}
